"use client";

import { useState, useCallback, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@ui/components/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/components/popover";
import { ChevronDownIcon, SearchIcon, TagIcon, Loader2Icon } from "lucide-react";
import { Input } from "@ui/components/input";
import { cn } from "@ui/lib";
import { PAGE_INTENTS, type PageIntent } from "@repo/core/client";
import { Badge } from "@ui/components/badge";
import { usePropertyPagesSearchQuery, type PageSearchResult } from "@saas/properties/lib/api";
import { useDebouncedValue } from "@shared/hooks/useDebouncedValue";

export interface PageData {
  path: string;
  title?: string;
  tags?: PageIntent[];
  count?: number;
}

interface PageDropdownProps {
  /** Property API key for fetching pages */
  propertyApiKey: string;
  /** Currently selected page path */
  selectedPage?: string;
  /** Display text for the selected page (optional, for when selection was made before) */
  selectedPageTitle?: string;
  /** Currently selected intents for filtering */
  selectedIntents: PageIntent[];
  /** Callback when a page is selected */
  onPageSelect: (path: string) => void;
  /** Callback when intents change */
  onIntentsChange: (intents: PageIntent[]) => void;
  /** Placeholder text when no page is selected */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
}

const MIN_SEARCH_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 300;

export function PageDropdown({
  propertyApiKey,
  selectedPage,
  selectedPageTitle,
  selectedIntents,
  onPageSelect,
  onIntentsChange,
  placeholder = "Search pages...",
  className
}: PageDropdownProps) {
  const t = useTranslations("properties.analytics");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Debounce the search query to avoid excessive API calls
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  
  // Fetch pages based on search query (only when 2+ chars)
  const { data: searchResults, isLoading: isSearching } = usePropertyPagesSearchQuery(
    propertyApiKey,
    debouncedSearch,
    { enabled: isOpen } // Only fetch when popover is open
  );
  
  // Reset search when popover closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  const handleToggleIntent = useCallback((intent: PageIntent) => {
    const newIntents = selectedIntents.includes(intent) 
      ? selectedIntents.filter(i => i !== intent)
      : [...selectedIntents, intent];
    
    onIntentsChange(newIntents);
    
    // If we're adding intent filters, clear any specific page selection
    if (newIntents.length > 0 && selectedPage) {
      onPageSelect("");
    }
  }, [selectedIntents, selectedPage, onIntentsChange, onPageSelect]);

  const getIntentDisplayName = useCallback((intent: PageIntent): string => {
    return t(`intents.${intent.toLowerCase()}` as any, { fallback: intent });
  }, [t]);
  
  // Determine what to show for selected page display
  const displayText = selectedPage 
    ? (selectedPageTitle || selectedPage) 
    : placeholder;

  const hasSearchQuery = searchQuery.length >= MIN_SEARCH_CHARS;
  const pages = searchResults || [];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className={cn("justify-between", className)}
        >
          <span className={cn("truncate", !selectedPage && "text-muted-foreground")}>
            {displayText}
          </span>
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3">
          {/* Search */}
          <div className="relative mb-3">
            <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Type to search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 text-sm pl-8"
              autoFocus
            />
            {isSearching && (
              <Loader2Icon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
            )}
          </div>

          {/* Intent Filters */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <TagIcon className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Filter by Intent
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
              {PAGE_INTENTS.map((intent, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-center justify-between p-1.5 rounded-sm cursor-pointer hover:bg-accent transition-colors text-xs",
                    selectedIntents.includes(intent) && "bg-accent"
                  )}
                  onClick={() => handleToggleIntent(intent)}
                >
                  <span>{getIntentDisplayName(intent)}</span>
                  {selectedIntents.includes(intent) && (
                    <Badge className="h-3 px-1 text-xs">✓</Badge>
                  )}
                </div>
              ))}
            </div>
            {selectedIntents.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onIntentsChange([])}
                className="h-6 px-2 text-xs mt-2 w-full"
              >
                Clear intent filters
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="border-t my-3" />

          {/* Pages Search Results */}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Pages
              </span>
              {hasSearchQuery && pages.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  ({pages.length}{pages.length === 50 ? "+" : ""})
                </span>
              )}
            </div>
            
            {!hasSearchQuery ? (
              <div className="text-center py-6 px-2">
                <SearchIcon className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">
                  Type at least {MIN_SEARCH_CHARS} characters to search pages
                </p>
              </div>
            ) : isSearching ? (
              <div className="text-center py-6">
                <Loader2Icon className="h-6 w-6 text-muted-foreground animate-spin mx-auto" />
                <p className="text-xs text-muted-foreground mt-2">Searching...</p>
              </div>
            ) : pages.length === 0 ? (
              <div className="text-center py-4 text-xs text-muted-foreground">
                No pages found for "{searchQuery}"
              </div>
            ) : (
              pages.map((page, index) => (
                <div
                  key={index}
                  className={cn(
                    "p-2 rounded-sm cursor-pointer hover:bg-accent transition-colors",
                    selectedPage === page.path && "bg-accent"
                  )}
                  onClick={() => {
                    onPageSelect(page.path);
                    // Clear intent filters when selecting a specific page
                    if (selectedIntents.length > 0) {
                      onIntentsChange([]);
                    }
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {page.title || page.path}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {page.path}
                      </div>
                      {page.tags && page.tags.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {page.tags.slice(0, 2).map((tag) => (
                            <Badge key={tag} className="h-3 px-1 text-xs">
                              {getIntentDisplayName(tag as PageIntent)}
                            </Badge>
                          ))}
                          {page.tags.length > 2 && (
                            <Badge className="h-3 px-1 text-xs">
                              +{page.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Clear selection button when a page is selected */}
          {selectedPage && (
            <>
              <div className="border-t my-3" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onPageSelect("");
                  setIsOpen(false);
                }}
                className="h-7 px-2 text-xs w-full"
              >
                Clear page filter
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Legacy PageDropdown that accepts pages as props
 * Use this when you already have pages loaded or need backward compatibility
 */
interface LegacyPageDropdownProps {
  pages: PageData[];
  selectedPage?: string;
  selectedIntents: PageIntent[];
  onPageSelect: (path: string) => void;
  onIntentsChange: (intents: PageIntent[]) => void;
  placeholder?: string;
  className?: string;
}

export function LegacyPageDropdown({
  pages,
  selectedPage,
  selectedIntents,
  onPageSelect,
  onIntentsChange,
  placeholder = "Select a page...",
  className
}: LegacyPageDropdownProps) {
  const t = useTranslations("properties.analytics");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleToggleIntent = (intent: PageIntent) => {
    const newIntents = selectedIntents.includes(intent) 
      ? selectedIntents.filter(i => i !== intent)
      : [...selectedIntents, intent];
    
    onIntentsChange(newIntents);
    
    // If we're adding intent filters, clear any specific page selection
    // since intent filtering and page selection are different modes
    if (newIntents.length > 0 && selectedPage) {
      onPageSelect("");
    }
  };

  const filteredPages = pages.filter(page => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      page.path.toLowerCase().includes(searchLower) ||
      (page.title && page.title.toLowerCase().includes(searchLower))
    );
  });

  const selectedPageData = pages.find(p => p.path === selectedPage);
  const getIntentDisplayName = (intent: PageIntent): string => {
    return t(`intents.${intent.toLowerCase()}` as any, { fallback: intent });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className={cn("justify-between", className)}
        >
          {selectedPageData ? (
            <span className="truncate">
              {selectedPageData.title || selectedPageData.path}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3">
          {/* Search */}
          <div className="relative mb-3">
            <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search pages by title or path..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 text-sm pl-8"
            />
          </div>

          {/* Intent Filters */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <TagIcon className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Filter by Intent
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
              {PAGE_INTENTS.map((intent, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-center justify-between p-1.5 rounded-sm cursor-pointer hover:bg-accent transition-colors text-xs",
                    selectedIntents.includes(intent) && "bg-accent"
                  )}
                  onClick={() => handleToggleIntent(intent)}
                >
                  <span>{getIntentDisplayName(intent)}</span>
                  {selectedIntents.includes(intent) && (
                    <Badge className="h-3 px-1 text-xs">✓</Badge>
                  )}
                </div>
              ))}
            </div>
            {selectedIntents.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onIntentsChange([])}
                className="h-6 px-2 text-xs mt-2 w-full"
              >
                Clear intent filters
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="border-t my-3" />

          {/* Pages */}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Pages
              </span>
              {filteredPages.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  ({filteredPages.length})
                </span>
              )}
            </div>
            
            {filteredPages.length === 0 ? (
              <div className="text-center py-4 text-xs text-muted-foreground">
                No pages found
              </div>
            ) : (
              filteredPages.map((page, index) => (
                <div
                  key={index}
                  className={cn(
                    "p-2 rounded-sm cursor-pointer hover:bg-accent transition-colors",
                    selectedPage === page.path && "bg-accent"
                  )}
                  onClick={() => {
                    onPageSelect(page.path);
                    // Clear intent filters when selecting a specific page
                    if (selectedIntents.length > 0) {
                      onIntentsChange([]);
                    }
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {page.title || page.path}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {page.path}
                      </div>
                      {page.tags && page.tags.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {page.tags.slice(0, 2).map((tag) => (
                            <Badge key={tag} className="h-3 px-1 text-xs">
                              {getIntentDisplayName(tag)}
                            </Badge>
                          ))}
                          {page.tags.length > 2 && (
                            <Badge className="h-3 px-1 text-xs">
                              +{page.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    {page.count && (
                      <div className="text-xs text-muted-foreground ml-2">
                        {page.count.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
